// Hours Calculator — work hours with break, overtime, wage logic

export interface HoursEntry {
  date: string; // ISO yyyy-mm-dd
  clockIn: string; // "HH:MM"
  clockOut: string; // "HH:MM"
  breakMinutes: number;
  notes?: string;
}

export interface HoursRowResult {
  grossHours: number;
  netHours: number;
  regularHours: number;
  overtimeHours: number;
  regularPay: number;
  overtimePay: number;
  totalPay: number;
}

export interface WageSettings {
  ratePerHour: number;
  dailyOTThreshold: number; // hours
  otMultiplier: number;
  weeklyHours: number;
}

export const DEFAULT_WAGE: WageSettings = {
  ratePerHour: 0,
  dailyOTThreshold: 8,
  otMultiplier: 1.5,
  weeklyHours: 48,
};

function parseHM(s: string): number | null {
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(s.trim());
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export function computeRow(entry: HoursEntry, wage: WageSettings): HoursRowResult {
  const inMin = parseHM(entry.clockIn);
  const outMin = parseHM(entry.clockOut);
  if (inMin === null || outMin === null) {
    return zeroRow();
  }
  let durMin = outMin - inMin;
  if (durMin < 0) durMin += 24 * 60; // overnight shift
  const grossHours = durMin / 60;
  const breakHrs = (entry.breakMinutes || 0) / 60;
  const netHours = Math.max(0, grossHours - breakHrs);
  const regularHours = Math.min(netHours, wage.dailyOTThreshold);
  const overtimeHours = Math.max(0, netHours - wage.dailyOTThreshold);
  const regularPay = regularHours * wage.ratePerHour;
  const overtimePay = overtimeHours * wage.ratePerHour * wage.otMultiplier;
  return {
    grossHours,
    netHours,
    regularHours,
    overtimeHours,
    regularPay,
    overtimePay,
    totalPay: regularPay + overtimePay,
  };
}

function zeroRow(): HoursRowResult {
  return {
    grossHours: 0,
    netHours: 0,
    regularHours: 0,
    overtimeHours: 0,
    regularPay: 0,
    overtimePay: 0,
    totalPay: 0,
  };
}

export interface PeriodSummary {
  totalGross: number;
  totalNet: number;
  totalRegular: number;
  totalOvertime: number;
  totalPay: number;
  weeklyOvertime: number; // additional weekly OT (hours over weekly cap not already counted as daily OT)
  rowResults: HoursRowResult[];
}

export function summarize(
  entries: HoursEntry[],
  wage: WageSettings,
  applyWeeklyOT = true,
): PeriodSummary {
  const rowResults = entries.map((e) => computeRow(e, wage));
  const totalGross = sum(rowResults.map((r) => r.grossHours));
  const totalNet = sum(rowResults.map((r) => r.netHours));
  let totalRegular = sum(rowResults.map((r) => r.regularHours));
  let totalOvertime = sum(rowResults.map((r) => r.overtimeHours));
  let totalPay = sum(rowResults.map((r) => r.totalPay));
  let weeklyOvertime = 0;

  if (applyWeeklyOT && wage.weeklyHours > 0 && totalRegular > wage.weeklyHours) {
    // Extra hours beyond weekly cap are reclassified as OT (only the excess)
    const extra = totalRegular - wage.weeklyHours;
    weeklyOvertime = extra;
    totalRegular -= extra;
    totalOvertime += extra;
    // Pay correction: those hours were paid as regular (1x), should be OT (otMultiplier)
    totalPay += extra * wage.ratePerHour * (wage.otMultiplier - 1);
  }

  return {
    totalGross,
    totalNet,
    totalRegular,
    totalOvertime,
    totalPay,
    weeklyOvertime,
    rowResults,
  };
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

export function formatHours(h: number): string {
  if (!Number.isFinite(h)) return "0h";
  const sign = h < 0 ? "-" : "";
  const abs = Math.abs(h);
  const whole = Math.floor(abs);
  const minutes = Math.round((abs - whole) * 60);
  if (minutes === 0) return `${sign}${whole}h`;
  return `${sign}${whole}h ${minutes}m`;
}
